import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card, Spinner } from "@nutrayan/ui";
import { formatCurrency, formatIndianDate } from "@nutrayan/utils";
import { apiClient } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useClaims } from "@/lib/use-claims";
import { hasPermission, roleLabel, tenantLabel, isPlatformTenant } from "@/lib/permissions";
import { NAV_SECTIONS } from "@/lib/nav";

// Contract: GET /admin/api/v1/analytics/executive-summary → DashboardSummary
interface DashboardSummary {
  from?: string;
  to?: string;
  totalGrossPaise?: number;
  totalPurchases?: number;
  totalCheckouts?: number;
}

// Contract: GET /admin/api/v1/orders → OrderSummary[]
interface OrderSummary {
  id: number;
  orderNumber?: string;
  status?: string;
  paymentMethod?: string;
  grandTotalPaise?: number;
  placedAt?: string;
}

export default function DashboardPage() {
  const auth = useAuth();
  const { role, tenantId } = useClaims();

  const canSeeAnalytics = hasPermission(role, "analytics.view");
  const canSeeOrders = hasPermission(role, "orders.manage");

  const summary = useQuery({
    queryKey: ["admin", "summary"],
    queryFn: () => apiClient.request<DashboardSummary>("/admin/api/v1/analytics/executive-summary"),
    enabled: canSeeAnalytics,
  });
  const recentOrders = useQuery({
    queryKey: ["admin", "recent-orders"],
    queryFn: () => apiClient.request<OrderSummary[]>("/admin/api/v1/orders?page=0&size=5"),
    enabled: canSeeOrders,
  });

  const s = summary.data;
  const aovPaise =
    s?.totalGrossPaise && s?.totalPurchases ? Math.round(s.totalGrossPaise / s.totalPurchases) : 0;

  // Quick actions = every nav destination this role can reach (minus the dashboard).
  const quickActions = NAV_SECTIONS.flatMap((section) => section.items).filter(
    (i) => i.to !== "/dashboard" && hasPermission(role, i.permission),
  );

  const name = auth.email?.split("@")[0] ?? "there";

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-display text-display-xl text-charcoal">Welcome back, {name}</h1>
        <p className="text-body text-ink mt-1">
          You&apos;re signed in as <strong>{roleLabel(role)}</strong> · Tenant:{" "}
          <strong>{tenantLabel(tenantId)}</strong>
        </p>
        {isPlatformTenant(tenantId) ? (
          <p className="text-caption text-muted mt-1">
            Platform-level account. Tenant-scoped lists (orders, catalog) reflect the platform
            tenant.
          </p>
        ) : null}
      </header>

      {/* KPIs — analytics-permitted roles only */}
      {canSeeAnalytics ? (
        summary.isPending ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : summary.isError ? (
          <Card>
            <p className="text-body text-terracotta">
              Couldn&apos;t load the executive summary. The backend may not have data yet.
            </p>
          </Card>
        ) : (
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <p className="text-caption text-muted">Purchases</p>
              <p className="font-display text-display-lg text-charcoal mt-1">
                {s?.totalPurchases ?? 0}
              </p>
            </Card>
            <Card>
              <p className="text-caption text-muted">Gross revenue</p>
              <p className="font-display text-display-lg text-charcoal mt-1">
                {formatCurrency(s?.totalGrossPaise ?? 0)}
              </p>
            </Card>
            <Card>
              <p className="text-caption text-muted">AOV</p>
              <p className="font-display text-display-lg text-charcoal mt-1">
                {formatCurrency(aovPaise)}
              </p>
            </Card>
            <Card>
              <p className="text-caption text-muted">Checkouts started</p>
              <p className="font-display text-display-lg text-charcoal mt-1">
                {s?.totalCheckouts ?? 0}
              </p>
            </Card>
          </section>
        )
      ) : null}

      {/* Quick actions — tailored to what this role can do */}
      {quickActions.length ? (
        <section>
          <h2 className="font-display text-display-sm text-charcoal">Your workspace</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {quickActions.map((a) => (
              <Link key={a.to} to={a.to}>
                <Card className="hover:border-saffron/40 flex items-center gap-3 transition-colors">
                  <span className="bg-forest/10 text-forest flex h-10 w-10 shrink-0 items-center justify-center rounded-md">
                    <a.icon size={20} aria-hidden />
                  </span>
                  <span className="text-body text-charcoal font-medium">{a.label}</span>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* Recent orders — orders-permitted roles only */}
      {canSeeOrders ? (
        <section>
          <h2 className="font-display text-display-sm text-charcoal">Recent orders</h2>
          {recentOrders.isPending ? (
            <Spinner className="mt-3" />
          ) : (recentOrders.data ?? []).length ? (
            <Card className="mt-3 overflow-x-auto p-0">
              <table className="text-body-sm w-full min-w-[32rem]">
                <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
                  <tr>
                    <th className="px-4 py-2">Order</th>
                    <th className="px-4 py-2">Placed</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Payment</th>
                    <th className="px-4 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(recentOrders.data ?? []).map((o) => (
                    <tr key={o.id} className="border-charcoal/5 border-b">
                      <td className="text-charcoal px-4 py-2 font-mono">{o.orderNumber ?? o.id}</td>
                      <td className="text-ink px-4 py-2">
                        {o.placedAt ? formatIndianDate(o.placedAt) : "—"}
                      </td>
                      <td className="text-ink px-4 py-2">{o.status ?? "—"}</td>
                      <td className="text-ink px-4 py-2">{o.paymentMethod ?? "—"}</td>
                      <td className="text-charcoal px-4 py-2 text-right">
                        {formatCurrency(o.grandTotalPaise ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : (
            <Card className="mt-3">
              <p className="text-body text-ink">No recent orders.</p>
            </Card>
          )}
        </section>
      ) : null}
    </div>
  );
}
